import { useCallback, useEffect, useState } from 'react';
import { buildQueryString, fmtDateTime, TABLE_HEAD_ROW_SX } from '../../Utils/format';
import {
  Box, Card, CardContent, Chip, Grid, LinearProgress, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import DeviceStatusIndicator from '../../Components/GatewayContagem/DeviceStatusIndicator';
import EventTimeline from '../../Components/GatewayContagem/EventTimeline';
import GatewayContagemFilters from '../../Components/GatewayContagem/GatewayContagemFilters';
import useGatewayContagem from '../../Context/Hooks/useGatewayContagem';
import { deviceAPI } from '../../API/deviceAPI';
import { eventAPI } from '../../API/eventAPI';

import LoadingSkeleton from '../../Components/GatewayContagem/LoadingSkeleton';

const StatusSummaryCard = ({ label, count, color, loading = false }) => (
  <Card>
    <CardContent sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Typography
        variant="overline"
        color="text.secondary"
      >{label}</Typography>
      <LoadingSkeleton loading={loading} preset="number">
        <Typography
          variant="h4"
          color={`${color}.main`}
        >{count}</Typography>
      </LoadingSkeleton>
    </CardContent>
  </Card>
);

const DeviceStatusPage = () => {
  const { t } = useTranslation(['devices', 'common', 'sidebar']);
  const { selectedClient, selectedStore, loadClients, clients, devices } = useGatewayContagem();
  const [clientDevices, setClientDevices] = useState([]);
  const [loadingClientDevices, setLoadingClientDevices] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [deviceEvents, setDeviceEvents] = useState([]);
  const [offlineDevices, setOfflineDevices] = useState([]);
  const [isLoading, setLoading] = useState(false);

  useEffect(() => { if (!clients.length) loadClients(); }, []);

  // Fetch client-level devices when a client is selected without a specific store
  useEffect(() => {
    if (!selectedClient?.id || selectedStore?.id) {
      setClientDevices([]);
      return;
    }
    let isSubscribed = true;
    setLoadingClientDevices(true);
    deviceAPI.list(buildQueryString({ clientIds: [selectedClient.id], page: 1, itemsPerPage: 200 }))
      .then((res) => {
        if (isSubscribed && res && !res.error) {
          setClientDevices(res.value?.items || []);
        }
      })
      .catch(() => {
        if (isSubscribed) {
          setClientDevices([]);
        }
      })
      .finally(() => {
        if (isSubscribed) {
          setLoadingClientDevices(false);
        }
      });

    return () => { isSubscribed = false; };
  }, [selectedClient?.id, selectedStore?.id]);

  const activeDevices = selectedStore?.id ? devices : clientDevices;

  const loadOffline = useCallback(async () => {
    if (!selectedClient?.id) {
      setOfflineDevices([]);
      return;
    }
    const data = await deviceAPI.getOffline(10, selectedClient.id, selectedStore?.id);
    if (data && !data.error) {
      setOfflineDevices(data.value || []);
    }
  }, [selectedClient?.id, selectedStore?.id]);

  useEffect(() => { loadOffline(); }, [loadOffline]);

  const loadDeviceEvents = useCallback(async (deviceId) => {
    setLoading(true);
    const data = await eventAPI.getRecentByDevice(deviceId, 48);
    setLoading(false);
    if (data && !data.error) {
      setDeviceEvents(data.value || []);
    } else {
      toast.error(t('feedback.errorLoadingEvents', 'Erro ao carregar eventos.'));
    }
  }, [t]);

  const handleSelectDevice = (device) => {
    setSelectedDevice(device);
    loadDeviceEvents(device.id);
  };

  const online = activeDevices.filter(d => d.status === 'ONLINE').length;
  const offline = activeDevices.filter(d => d.status === 'OFFLINE').length;
  const error = activeDevices.filter(d => d.status === 'ERROR').length;
  const maintenance = activeDevices.filter(d => d.status === 'MAINTENANCE').length;

  return (
    <Box sx={{ p: 3 }}>
      {isLoading && <LinearProgress sx={{ position: 'absolute', top: 64, left: 0, width: '100%' }} />}
      <Typography
        variant="h5"
        mb={2}
      >
        {t('sidebar:gateway.estadoDispositivo', 'Estado de Dispositivos')}
      </Typography>

      <Box mb={2}>
        <GatewayContagemFilters
          showClient
          showStore
          showDateRange={false}
        />
      </Box>

      <Grid
        container
        spacing={2}
        mb={3}
      >
        <Grid
          item
          xs={6}
          sm={3}
        >
          <StatusSummaryCard
            label={t('common:deviceStatus.online', 'Online')}
            count={online}
            color="success"
          />
        </Grid>
        <Grid
          item
          xs={6}
          sm={3}
        >
          <StatusSummaryCard
            label={t('common:deviceStatus.offline', 'Offline')}
            count={offline}
            color="error"
          />
        </Grid>
        <Grid
          item
          xs={6}
          sm={3}
        >
          <StatusSummaryCard
            label={t('common:deviceStatus.error', 'Erro')}
            count={error}
            color="error"
          />
        </Grid>
        <Grid
          item
          xs={6}
          sm={3}
        >
          <StatusSummaryCard
            label={t('common:deviceStatus.maintenance', 'Manutenção')}
            count={maintenance}
            color="warning"
          />
        </Grid>
      </Grid>

      {offlineDevices.length > 0 && (
        <Card sx={{ mb: 2, border: '1px solid', borderColor: 'error.light' }}>
          <CardContent>
            <Typography
              variant="subtitle2"
              color="error"
              gutterBottom
            >
              {t('common:deviceStatus.offline', 'Offline')} ({offlineDevices.length})
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              flexWrap="wrap"
            >
              {offlineDevices.map(d => (
                <Chip
                  key={d.id}
                  label={d.name}
                  color="error"
                  variant="outlined"
                  size="small"
                  onClick={() => handleSelectDevice(d)}
                />
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Grid
        container
        spacing={2}
      >
        <Grid
          item
          xs={12}
          md={7}
        >
          <LoadingSkeleton loading={isLoading || loadingClientDevices} preset="table" height={260}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={TABLE_HEAD_ROW_SX}>
                    <TableCell>{t('common:common.device', 'Dispositivo')}</TableCell>
                    <TableCell>{t('devices:table.status', 'Estado')}</TableCell>
                    <TableCell>{t('common:deviceStatus.lastSeen', 'Último contacto')}</TableCell>
                    <TableCell>{t('devices:table.location', 'Localização')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeDevices.map(d => (
                    <TableRow
                      key={d.id}
                      hover
                      selected={selectedDevice?.id === d.id}
                      onClick={() => handleSelectDevice(d)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{d.name}</TableCell>
                      <TableCell>
                        <DeviceStatusIndicator
                          status={d.status}
                          lastSeen={d.lastHeartbeat}
                        />
                      </TableCell>
                      <TableCell>{d.lastHeartbeat ? fmtDateTime(d.lastHeartbeat) : '—'}</TableCell>
                      <TableCell>{d.location || '—'}</TableCell>
                    </TableRow>
                  ))}
                  {!activeDevices.length && (
                    <TableRow>
                      <TableCell
                        colSpan={4}
                        align="center"
                      >
                        {selectedStore || selectedClient
                          ? t('devices:table.empty', 'Nenhum dispositivo encontrado.')
                          : t('common:storeRequired', 'Selecione uma loja')}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </LoadingSkeleton>
        </Grid>

        <Grid
          item
          xs={12}
          md={5}
        >
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography
                variant="subtitle2"
                gutterBottom
              >
                {selectedDevice
                  ? `${t('devices:detail.events.title', 'Eventos recentes')} — ${selectedDevice.name}`
                  : t('devices:detail.events.selectDevice', 'Selecione um dispositivo para ver os eventos')}
              </Typography>
              <EventTimeline
                events={deviceEvents}
                maxHeight={400}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DeviceStatusPage;
